import { Abi, Address } from 'viem'
import { useMemo } from 'react'
import { WNATIVE } from '@pulsex/sdk'
import { erc20ABI, useWalletClient } from 'wagmi'
import { useActiveChainId } from 'hooks/useActiveChainId'

import { getMulticallAddress } from 'utils/addressHelpers'
import {
  getContract,
  getPlsxContract,
  getMasterChefContract,
  getPlsxLabContract,
  getBuyAndBurnContract,
} from 'utils/contractHelpers'

import { multicallAbi } from 'config/abi/multicall'
import { erc20BytesAbi } from 'config/abi/erc20_bytes32'
import { pulsexPairAbi } from 'config/abi/IPulseXPair'
import { wethAbi } from 'config/abi/weth'

/**
 * Helper hooks to get specific contracts (by ABI)
 */

export const useERC20 = (address: Address) => {
  return useContract(address, erc20ABI)
}

export const usePlsx = () => {
  const { chainId } = useActiveChainId()
  return useMemo(() => getPlsxContract(chainId), [chainId])
}

export const useMasterchef = () => {
  const { chainId } = useActiveChainId()
  const { data: signer } = useWalletClient()
  return useMemo(() => getMasterChefContract(signer ?? undefined, chainId), [signer, chainId])
}

export const usePlsxLab = () => {
  const { data: signer } = useWalletClient()
  return useMemo(() => getPlsxLabContract(signer), [signer])
}

export const useBuyAndBurnContract = (protocol: string) => {
  const { chainId } = useActiveChainId()
  const { data: signer } = useWalletClient()
  return useMemo(() => getBuyAndBurnContract(protocol, signer, chainId), [chainId, protocol, signer])
}

// Code below migrated from Exchange useContract.ts

// returns null on errors
export function useContract<TAbi extends Abi>(
  addressOrAddressMap?: Address | { [chainId: number]: Address },
  abi?: TAbi,
) {
  const { chainId } = useActiveChainId()
  const { data: walletClient } = useWalletClient()

  return useMemo(() => {
    if (!addressOrAddressMap || !abi || !chainId) return null
    let address: Address | undefined
    if (typeof addressOrAddressMap === 'string') address = addressOrAddressMap
    else address = addressOrAddressMap[chainId]
    if (!address) return null
    try {
      return getContract({
        abi,
        address,
        chainId,
        signer: walletClient ?? undefined,
      })
    } catch (error) {
      console.error('Failed to get contract', error)
      return null
    }
  }, [addressOrAddressMap, abi, chainId, walletClient])
}

export function useTokenContract(tokenAddress?: Address) {
  return useContract(tokenAddress, erc20ABI)
}

export function useWETHContract() {
  const { chainId } = useActiveChainId()
  return useContract(chainId ? WNATIVE[chainId]?.address : undefined, wethAbi)
}

export function useBytes32TokenContract(tokenAddress?: Address) {
  return useContract(tokenAddress, erc20BytesAbi)
}

export function usePairContract(pairAddress?: Address) {
  return useContract(pairAddress, pulsexPairAbi)
}

export function useMulticallContract() {
  const { chainId } = useActiveChainId()
  return useContract(getMulticallAddress(chainId), multicallAbi)
}
